import { useSearchParams } from "react-router-dom";
import { Phone, MapPin, Clock, Navigation } from "lucide-react";
import Seo from "@/components/Seo";
import BookingForm from "@/components/BookingForm";
import { Reveal, SectionHeading } from "@/components/Reveal";
import { CONTACT, SERVICES } from "@/data/site";

export default function Contact() {
  const [params] = useSearchParams();
  const serviceParam = params.get("service") || "";
  const initialService = SERVICES.some((s) => s.id === serviceParam) ? serviceParam : "";

  return (
    <main data-testid="contact-page" className="pt-16">
      <Seo
        title="Book & Contact — Bowegans Car Care, Palani"
        siteName="Bowegans Car Care"
        description="Book a service slot at Bowegans Car Care, NH.83 Bypass Road, Palani. Call +91 70100 80038 or send a booking request online. Open all 7 days."
      />
      <section className="border-b border-white/10 bg-[#0E1013]">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6">
          <SectionHeading
            testid="contact-page-heading"
            eyebrow="Book & contact"
            title="Reserve your bay."
            description="Send a booking request and we will call you back to confirm your slot — or just call us directly."
          />
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6">
        <div className="grid gap-10 lg:grid-cols-[1fr_1.2fr]">
          <div className="space-y-5">
            <Reveal>
              <a
                href={CONTACT.phoneLink}
                data-testid="contact-phone-display"
                className="spotlight-card group flex items-center gap-5 border border-[#262A35] bg-[#121418] p-6 transition-colors hover:border-[#3B82F6]/60"
              >
                <span className="flex h-12 w-12 shrink-0 items-center justify-center bg-[#3B82F6]">
                  <Phone className="h-5 w-5 text-white" />
                </span>
                <span>
                  <span className="font-mono2 block text-[10px] uppercase tracking-[0.25em] text-gray-500">
                    Call us directly
                  </span>
                  <span className="font-display mt-1 block text-xl font-bold text-white">
                    {CONTACT.phoneDisplay}
                  </span>
                  <a
                    href={CONTACT.phone2Link}
                    data-testid="contact-phone2-display"
                    className="font-mono2 mt-1 block text-xs tracking-widest text-gray-400 transition-colors hover:text-white"
                  >
                    Alternate: {CONTACT.phone2Display}
                  </a>
                </span>
              </a>
            </Reveal>
            <Reveal delay={0.08}>
              <div className="spotlight-card border border-[#262A35] bg-[#121418] p-6" data-testid="contact-address-display">
                <div className="flex items-start gap-5">
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center border border-[#3A3F50]">
                    <MapPin className="h-5 w-5 text-[#3B82F6]" />
                  </span>
                  <div>
                    <p className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-gray-500">Find us at</p>
                    <p className="font-display mt-1 text-lg font-bold leading-snug text-white">{CONTACT.address}</p>
                    <a
                      href={CONTACT.mapsUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="contact-directions-button"
                      className="font-mono2 mt-4 inline-flex items-center gap-2 border border-white/15 px-4 py-2 text-[10px] font-bold uppercase tracking-[0.2em] text-gray-300 transition-colors hover:border-[#3B82F6] hover:text-white"
                    >
                      <Navigation className="h-3.5 w-3.5 text-[#3B82F6]" />
                      Open in Google Maps
                    </a>
                  </div>
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.16}>
              <div className="spotlight-card flex items-center gap-5 border border-[#262A35] bg-[#121418] p-6" data-testid="contact-hours-display">
                <span className="flex h-12 w-12 shrink-0 items-center justify-center border border-[#3A3F50]">
                  <Clock className="h-5 w-5 text-[#3B82F6]" />
                </span>
                <div>
                  <p className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-gray-500">Working hours</p>
                  <p className="font-display mt-1 text-base font-bold leading-snug text-white sm:text-lg">{CONTACT.hours}</p>
                </div>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.1}>
            <div className="border border-[#262A35] bg-[#121418] p-6 sm:p-8">
              <p className="font-mono2 text-xs uppercase tracking-[0.3em] text-[#3B82F6]">Booking request</p>
              <h2 className="font-display mt-2 text-xl font-bold text-white sm:text-2xl">
                Tell us about your car
              </h2>
              <div className="mt-6">
                <BookingForm key={initialService} initialService={initialService} />
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
